import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";
import { Reveal, SectionHead } from "@/components/Reveal";

export default function FAQPage() {
  const { faqs } = useSite();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const categories = useMemo(() => {
    const set = new Set((faqs || []).map((f) => f.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [faqs]);

  const list = (faqs || []).filter((f) => {
    const matchCat = category === "All" || f.category === category;
    const q = query.trim().toLowerCase();
    const matchQuery = !q || (f.question || "").toLowerCase().includes(q) || (f.answer || "").toLowerCase().includes(q);
    return matchCat && matchQuery;
  });

  return (
    <div className="pt-24 sm:pt-28" data-testid="faq-page">
      <section className="relative py-14 sm:py-20 overflow-hidden">
        <div className="ambient-glow w-[560px] h-[300px] -top-32 left-1/2 -translate-x-1/2 opacity-40" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <SectionHead
            eyebrow="Help Center"
            title="Frequently asked questions"
            sub="Search or browse — straight answers, no fine print."
          />
          <Reveal>
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-subtle" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search questions — pricing, timeline, ads..."
                className="w-full glass rounded-full pl-11 pr-5 py-3.5 text-sm text-white placeholder:text-subtle outline-none focus:border-accent/60 transition-colors"
                data-testid="faq-search-input"
              />
            </div>
            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all duration-300 ${category === c ? "bg-accent border-accent text-white" : "border-white/15 text-mist hover:text-white hover:border-accent/50"}`}
                  data-testid={`faq-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>

          {list.length === 0 ? (
            <p className="text-center text-sm text-subtle py-16" data-testid="faq-empty-state">No questions matched "{query}". Try another keyword.</p>
          ) : (
            <Accordion type="single" collapsible className="space-y-3">
              {list.map((f, i) => (
                <Reveal key={f.id || i} delay={i * 0.04}>
                  <AccordionItem value={`faq-${i}`} className="glass rounded-2xl border-none px-6 mb-3">
                    <AccordionTrigger className="text-left text-sm sm:text-base font-semibold text-white hover:no-underline py-5" data-testid={`faq-page-trigger-${i}`}>
                      {f.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-mist leading-relaxed pb-5">{f.answer}</AccordionContent>
                  </AccordionItem>
                </Reveal>
              ))}
            </Accordion>
          )}
        </div>
      </section>
    </div>
  );
}
